import React, { useState, useEffect, useCallback } from 'react';
import {
  Gift,
  Users,
  DollarSign,
  Share2,
  Copy,
  Check,
  Clock,
  Mail,
  Tag,
  Banknote,
  AlertCircle,
  Pencil,
  X,
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import {
  referralManager,
  type Referral,
  type ReferralStats,
  type ReferralProgramData,
} from '@/business/referral/ReferralManager';
import { Modal, Button, IconButton, Input, TabButton, TabGroup } from '@/components/ui';

type TabType = 'share' | 'referrals' | 'earnings';

export interface ReferAndEarnModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Refer & Earn — opened from the sidebar. The share tab leads with the
 * referral link: the 30% first-month discount is applied automatically when a
 * friend signs up through it, so there is no code for anyone to enter.
 */
export const ReferAndEarnModal: React.FC<ReferAndEarnModalProps> = ({ isOpen, onClose }) => {
  const { success: showSuccess, error: showError } = useToast();

  const [activeTab, setActiveTab] = useState<TabType>('share');
  const [copied, setCopied] = useState(false);
  const [programData, setProgramData] = useState<ReferralProgramData | null>(null);
  const [loading, setLoading] = useState(true);

  const [enrolling, setEnrolling] = useState(false);
  const [paypalModalOpen, setPaypalModalOpen] = useState(false);
  const [paypalEmailInput, setPaypalEmailInput] = useState('');
  const [savingPaypal, setSavingPaypal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await referralManager.getReferralProgram();
      setProgramData(data);
    } catch (error) {
      console.error('Failed to load referral data:', error);
      showError('Failed to load referral program data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('share');
    loadData();
  }, [isOpen, loadData]);

  const handleEnroll = useCallback(async () => {
    try {
      setEnrolling(true);
      const data = await referralManager.enroll();
      setProgramData(data);
      if (data.isEnrolled) {
        showSuccess('Your referral link is active!');
      } else {
        showError('Could not activate your referral link. Please try again.');
      }
    } catch (error) {
      console.error('Failed to enroll in referral program:', error);
      showError('Could not activate your referral link. Please try again.');
    } finally {
      setEnrolling(false);
    }
  }, [showSuccess, showError]);

  const referralLink = programData?.referralLink ?? '';

  const handleCopyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available — silently ignore
    }
  };

  const handleShare = async () => {
    if (!referralLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Routemize',
          text: 'I love using Routemize for scheduling. Sign up with my link and get 30% off your first month.',
          url: referralLink,
        });
        return;
      } catch {
        // user cancelled or share unavailable — fall through to copy
      }
    }
    handleCopyLink();
  };

  const openPaypalModal = () => {
    setPaypalEmailInput(programData?.payPalEmail ?? '');
    setPaypalModalOpen(true);
  };

  const handleSavePaypal = async () => {
    if (!paypalEmailInput.trim()) {
      showError('Please enter a valid PayPal email.');
      return;
    }
    setSavingPaypal(true);
    try {
      await referralManager.updatePayPalEmail(paypalEmailInput.trim());
      setProgramData((prev) => (prev ? { ...prev, payPalEmail: paypalEmailInput.trim() } : prev));
      setPaypalModalOpen(false);
      showSuccess('PayPal email saved. The Routemize team will use this for your next payout.');
    } catch {
      showError('Failed to save PayPal email. Please try again.');
    } finally {
      setSavingPaypal(false);
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'share', label: 'Share', icon: <Share2 size={15} /> },
    { id: 'referrals', label: 'Referrals', icon: <Users size={15} /> },
    { id: 'earnings', label: 'Earnings', icon: <DollarSign size={15} /> },
  ];

  const enrolled = Boolean(programData?.isEnrolled) && !loading;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" hideHeader>
        {/* Hero */}
        <div className="relative bg-gradient-to-r from-success-500 to-accent-500 px-6 py-5">
          <IconButton
            icon={X}
            shape="square"
            size="sm"
            aria-label="Close"
            onClick={onClose}
            iconClassName="h-4 w-4"
            className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 focus-visible:ring-white/60"
          />

          <div className="flex items-start gap-4 pr-8">
            <div className="p-2.5 bg-white/20 rounded-xl ring-1 ring-white/25">
              <Gift className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Refer & Earn $50</h2>
              <p className="text-white/90 text-sm mt-1">
                Share your link and earn <span className="font-semibold text-white">$50</span> per referral. They get{' '}
                <span className="font-semibold text-white">30% off their first month</span>.
              </p>
            </div>
          </div>

          {enrolled && programData && (
            <div className="grid grid-cols-3 gap-4 mt-5 pt-4 border-t border-white/20">
              <HeroStat label="Referrals" value={String(programData.stats.totalReferrals)} />
              <HeroStat
                label="Total earned"
                value={referralManager.formatCurrency(programData.stats.totalEarnings)}
              />
              <HeroStat
                label="Available"
                value={referralManager.formatCurrency(programData.stats.availableForPayout)}
              />
            </div>
          )}
        </div>

        {enrolled && (
          <div className="px-6 pt-4">
            <TabGroup>
              {tabs.map((tab) => (
                <TabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
                  {tab.icon}
                  {tab.label}
                </TabButton>
              ))}
            </TabGroup>
          </div>
        )}

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent-500 border-t-transparent" />
            </div>
          ) : !programData?.isEnrolled ? (
            <NotEnrolledState onEnroll={handleEnroll} enrolling={enrolling} />
          ) : (
            <>
              {activeTab === 'share' && (
                <ShareTab
                  referralLink={referralLink}
                  copied={copied}
                  onCopyLink={handleCopyLink}
                  onShare={handleShare}
                />
              )}
              {activeTab === 'referrals' && <ReferralsTab referrals={programData.referrals} />}
              {activeTab === 'earnings' && (
                <EarningsTab
                  stats={programData.stats}
                  payPalEmail={programData.payPalEmail}
                  onEditPayPal={openPaypalModal}
                />
              )}
            </>
          )}
        </div>
      </Modal>

      {/* PayPal email dialog — stacks above the referral modal */}
      <Modal
        isOpen={paypalModalOpen}
        onClose={() => setPaypalModalOpen(false)}
        title="PayPal Email for Payouts"
        titleIcon={<Banknote size={18} />}
        description="The Routemize team uses this address to send your commission payouts via PayPal."
        size="sm"
        zIndex={110}
        dismissOnBackdrop={!savingPaypal}
        dismissOnEscape={!savingPaypal}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPaypalModalOpen(false)} disabled={savingPaypal}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={savingPaypal} onClick={handleSavePaypal}>
              Save
            </Button>
          </div>
        }
      >
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="paypal-email" className="block text-sm font-medium text-ink-700 mb-1.5">
              PayPal email address
            </label>
            <Input
              id="paypal-email"
              type="email"
              placeholder="you@example.com"
              value={paypalEmailInput}
              onChange={(e) => setPaypalEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSavePaypal();
              }}
              disabled={savingPaypal}
            />
          </div>
          <p className="text-xs text-ink-500">
            Payouts are processed manually by the Routemize team. Saving your PayPal email here ensures we can reach
            you when your balance is ready to pay out.
          </p>
        </div>
      </Modal>
    </>
  );
};

const HeroStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-xl font-bold text-white leading-tight">{value}</p>
    <p className="text-xs text-white/80 mt-0.5">{label}</p>
  </div>
);

const NotEnrolledState: React.FC<{ onEnroll: () => void; enrolling: boolean }> = ({ onEnroll, enrolling }) => (
  <div className="text-center py-8">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-ink-100 flex items-center justify-center">
      <Gift size={30} className="text-ink-400" />
    </div>
    <h3 className="text-lg font-semibold text-ink-900 mb-1.5">Activate your referral link</h3>
    <p className="text-sm text-ink-500 max-w-sm mx-auto mb-6">
      Get a shareable link that gives every business you refer 30% off their first month — and pays you $50.
    </p>
    <Button onClick={onEnroll} loading={enrolling}>
      {enrolling ? 'Activating…' : 'Activate referral link'}
    </Button>
  </div>
);

const ShareTab: React.FC<{
  referralLink: string;
  copied: boolean;
  onCopyLink: () => void;
  onShare: () => void;
}> = ({ referralLink, copied, onCopyLink, onShare }) => (
  <div className="space-y-5">
    {/* The link is the whole product — everything else supports it */}
    <div>
      <label className="block text-sm font-medium text-ink-700 mb-2">Your referral link</label>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 truncate rounded-lg border border-ink-200 bg-ink-100 px-4 py-2.5 font-mono text-sm text-ink-700">
          {referralLink}
        </div>
        <Button onClick={onCopyLink} className="flex-shrink-0">
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </div>
      <div className="flex gap-2 mt-3">
        <Button variant="secondary" className="flex-1" onClick={onShare}>
          <Share2 size={16} />
          Share link
        </Button>
        <Button
          variant="secondary"
          className="flex-1"
          onClick={() =>
            window.open(
              `mailto:?subject=Join%20Routemize%20-%20Smart%20Scheduling&body=Hey!%0A%0AI've%20been%20using%20Routemize%20for%20scheduling%20and%20it's%20been%20amazing.%20Sign%20up%20with%20my%20link%20and%20you'll%20get%2030%25%20off%20your%20first%20month.%0A%0A${encodeURIComponent(referralLink)}%0A%0ACheers!`,
              '_blank',
            )
          }
        >
          <Mail size={16} />
          Email
        </Button>
      </div>
    </div>

    {/* Why the link is worth clicking */}
    <div className="flex items-start gap-3 rounded-xl border border-success-200 bg-success-50 p-4">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-success-100">
        <Tag size={18} className="text-success-600" />
      </div>
      <div>
        <p className="font-semibold text-ink-900 text-sm">Anyone who signs up gets 30% off their first month</p>
        <p className="text-sm text-ink-600 mt-0.5">
          The discount is applied automatically — there's no code for them to enter.
        </p>
      </div>
    </div>

    {/* How it works */}
    <div className="border-t border-ink-200 pt-4">
      <div className="grid grid-cols-3 gap-4">
        {[
          { title: 'Share your link', desc: 'Send it to friends and colleagues.' },
          { title: 'They save 30%', desc: 'Applied to their first month at signup.' },
          { title: 'You earn $50', desc: 'Paid after their first charge clears.' },
        ].map((step, idx) => (
          <div key={idx} className="flex gap-2.5">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-semibold text-ink-600">
              {idx + 1}
            </span>
            <div>
              <p className="text-sm font-medium text-ink-900">{step.title}</p>
              <p className="text-xs text-ink-500 mt-0.5 leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ReferralsTab: React.FC<{ referrals: Referral[] }> = ({ referrals }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-base font-semibold text-ink-900">Your referrals</h3>
      <span className="text-sm text-ink-500">{referrals.length} total</span>
    </div>

    {referrals.length === 0 ? (
      <div className="text-center py-10 bg-ink-100 rounded-xl">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-ink-200 flex items-center justify-center">
          <Users size={26} className="text-ink-400" />
        </div>
        <h4 className="text-base font-semibold text-ink-900 mb-1">No referrals yet</h4>
        <p className="text-sm text-ink-500">Share your link to start earning.</p>
      </div>
    ) : (
      <div className="space-y-3 max-h-[45vh] overflow-y-auto">
        {referrals.map((referral) => {
          const statusDisplay = referralManager.getStatusDisplay(referral.status);
          // Fall back to the email local-part when FP has no name on the referral.
          const displayName =
            referral.referredName?.trim() || referral.referredEmail?.split('@')[0] || 'Unknown';
          const initial = displayName.charAt(0).toUpperCase();
          return (
            <div key={referral.id} className="p-4 rounded-xl bg-ink-100 border border-ink-200">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 flex-shrink-0 rounded-full bg-accent-500 flex items-center justify-center text-white font-semibold">
                    {initial}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-ink-900 truncate">{displayName}</p>
                    <p className="text-sm text-ink-500 truncate">{referral.referredEmail}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusDisplay.bgColor} ${statusDisplay.color}`}
                  >
                    {statusDisplay.text}
                  </span>
                  {referral.earnedAmount > 0 && (
                    <p className="text-sm font-bold text-success-600 mt-1">
                      +{referralManager.formatCurrency(referral.earnedAmount)}
                    </p>
                  )}
                </div>
              </div>

              {referral.qualificationProgress > 0 && referral.qualificationProgress < 100 && (
                <div className="mt-3 pt-3 border-t border-ink-200">
                  <div className="flex items-center justify-between text-xs text-ink-600 mb-1.5">
                    <span>Qualification progress</span>
                    <span className="font-medium">{referral.qualificationProgress}%</span>
                  </div>
                  <div className="h-2 bg-ink-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-success-500 rounded-full transition-all"
                      style={{ width: `${referral.qualificationProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 mt-3 text-xs text-ink-500">
                <Clock size={13} />
                <span>Signed up {referralManager.formatDate(referral.signupDate)}</span>
                {referral.conversionDate && (
                  <>
                    <span className="text-ink-300">•</span>
                    <span>Converted {referralManager.formatDate(referral.conversionDate)}</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const EarningsTab: React.FC<{
  stats: ReferralStats;
  payPalEmail: string | null;
  onEditPayPal: () => void;
}> = ({ stats, payPalEmail, onEditPayPal }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="p-4 rounded-xl bg-success-50 border border-success-200">
        <p className="text-sm font-medium text-ink-600 mb-1">Available for payout</p>
        <p className="text-2xl font-bold text-success-700">
          {referralManager.formatCurrency(stats.availableForPayout)}
        </p>
      </div>
      <div className="p-4 rounded-xl bg-ink-100 border border-ink-200">
        <p className="text-sm font-medium text-ink-600 mb-1">Lifetime earnings</p>
        <p className="text-2xl font-bold text-ink-900">{referralManager.formatCurrency(stats.totalEarnings)}</p>
        <p className="text-xs text-ink-500 mt-1">
          {stats.convertedReferrals} successful referral{stats.convertedReferrals !== 1 ? 's' : ''}
        </p>
      </div>
    </div>

    {(stats.pendingEarnings > 0 || stats.lifetimePayouts > 0) && (
      <div className="grid grid-cols-2 gap-4">
        {stats.pendingEarnings > 0 && (
          <div className="p-4 rounded-xl bg-ink-100 border border-ink-200">
            <p className="text-xs font-medium text-ink-600 mb-1">Pending earnings</p>
            <p className="text-xl font-bold text-ink-900">{referralManager.formatCurrency(stats.pendingEarnings)}</p>
            <p className="text-xs text-ink-500 mt-0.5">Referrals not yet converted</p>
          </div>
        )}
        {stats.lifetimePayouts > 0 && (
          <div className="p-4 rounded-xl bg-ink-100 border border-ink-200">
            <p className="text-xs font-medium text-ink-600 mb-1">Lifetime payouts</p>
            <p className="text-xl font-bold text-ink-900">{referralManager.formatCurrency(stats.lifetimePayouts)}</p>
            <p className="text-xs text-ink-500 mt-0.5">Already paid out to you</p>
          </div>
        )}
      </div>
    )}

    <div className="p-4 rounded-xl bg-ink-100 border border-ink-200">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Banknote size={17} className="text-ink-600" />
          <p className="font-semibold text-ink-900">Payout via PayPal</p>
        </div>
        <Button variant="link" size="sm" onClick={onEditPayPal}>
          <Pencil size={14} />
          {payPalEmail ? 'Edit' : 'Add email'}
        </Button>
      </div>
      {payPalEmail ? (
        <p className="text-ink-700 font-mono text-sm">{payPalEmail}</p>
      ) : (
        <p className="text-ink-500 text-sm">
          No PayPal email set. Add one so the Routemize team can pay you when your balance is ready.
        </p>
      )}
    </div>

    <div className="flex gap-3 p-4 rounded-xl bg-accent-50 border border-accent-200">
      <AlertCircle size={18} className="text-accent-600 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-ink-700">
        Payouts are processed manually by the Routemize team — you don't need to request them. We'll reach out via
        PayPal once your balance is ready to send.
      </p>
    </div>
  </div>
);

export default ReferAndEarnModal;
